import type { FC, ReactNode } from 'react'

import { useDictionary } from '@/lib/i18n'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

const RacingSection: FC = (): ReactNode => {
    const t = useDictionary()
    const { racing } = t

    return (
        <section id='racing' aria-labelledby='racing-title' className='relative py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='grid gap-8 lg:grid-cols-2 lg:items-end lg:gap-16'>
                    <Reveal>
                        <SectionHeading id='racing-title' eyebrow={racing.eyebrow} title={racing.title} />
                    </Reveal>

                    <Reveal delay={100}>
                        <p className='text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg lg:pb-2'>
                            {racing.intro}
                        </p>
                    </Reveal>
                </div>

                <Reveal className='mt-14'>
                    <dl className='grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-3'>
                        {racing.stats.map((stat) => (
                            <div key={stat.label} className='flex flex-col bg-background p-6 sm:p-8'>
                                <dt className='order-2 mt-5 text-base font-semibold text-foreground'>
                                    {stat.label}
                                </dt>

                                <dd className='order-1 font-heading text-7xl font-bold leading-none tracking-tight text-foreground'>
                                    {stat.value}
                                </dd>

                                <dd className='order-3 mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                    {stat.detail}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </Reveal>

                <Reveal className='mt-8'>
                    <div className='overflow-hidden rounded-2xl border border-border bg-card/60'>
                        <div className='flex items-center justify-between gap-4 border-b border-border px-5 py-4 sm:px-6'>
                            <h3 className='display-heading font-heading text-xl font-semibold uppercase tracking-wide text-foreground'>
                                {racing.board.title}
                            </h3>

                            <span className='inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-race-bright'>
                                <span aria-hidden='true' className='size-1.5 rounded-full bg-race-bright motion-safe:animate-pulse' />
                                2026
                            </span>
                        </div>

                        <div className='overflow-x-auto'>
                            <table className='w-full min-w-[34rem] text-left text-sm'>
                                <caption className='sr-only'>
                                    {racing.board.caption}
                                </caption>

                                <thead>
                                    <tr className='font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground'>
                                        <th scope='col' className='px-5 py-3 font-normal sm:px-6'>
                                            {racing.board.columns.rider}
                                        </th>
                                        <th scope='col' className='px-5 py-3 font-normal sm:px-6'>
                                            {racing.board.columns.result}
                                        </th>
                                        <th scope='col' className='px-5 py-3 text-right font-normal sm:px-6'>
                                            {racing.board.columns.time}
                                        </th>
                                    </tr>
                                </thead>

                                <tbody className='divide-y divide-border border-t border-border'>
                                    {racing.board.rows.map((row, index) => (
                                        <tr key={row.rider} className='transition-colors hover:bg-white/[0.03]'>
                                            <th scope='row' className='px-5 py-5 font-heading text-xl font-semibold uppercase tracking-wide text-foreground sm:px-6'>
                                                <span className='flex items-center gap-3'>
                                                    <span className='font-mono text-xs font-normal text-muted-foreground'>
                                                        {String(index + 1).padStart(2, '0')}
                                                    </span>
                                                    {row.rider}
                                                </span>
                                            </th>

                                            <td className='px-5 py-5 sm:px-6'>
                                                <span className='flex items-center gap-2 font-semibold text-foreground'>
                                                    {index === 0 && (
                                                        <span aria-hidden='true' className='size-2 rounded-full bg-[#f2c94c]' />
                                                    )}
                                                    {row.result}
                                                </span>
                                                <span className='mt-0.5 block text-xs text-muted-foreground'>
                                                    {row.race}
                                                </span>
                                            </td>

                                            <td className='px-5 py-5 text-right sm:px-6'>
                                                <span className='block font-mono text-base tabular-nums text-foreground'>
                                                    {row.time}
                                                </span>
                                                <span className='mt-0.5 block font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground'>
                                                    {row.timeNote}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Reveal>

                <div className='mt-16'>
                    <h3 className='font-mono text-[11px] uppercase tracking-[0.24em] text-muted-foreground'>
                        {racing.disciplines.title}
                    </h3>

                    <ul className='mt-5 grid gap-5 sm:grid-cols-3'>
                        {racing.disciplines.items.map((item, index) => (
                            <li key={item.name}>
                                <Reveal delay={index * 80} className='h-full'>
                                    <div className='h-full rounded-2xl border border-border p-6 transition-colors duration-300 hover:border-race/40'>
                                        <p className='font-mono text-[11px] uppercase tracking-[0.2em] text-race-bright'>
                                            {item.label}
                                        </p>
                                        <p className='mt-3 font-heading text-3xl font-semibold uppercase tracking-tight text-foreground'>
                                            {item.name}
                                        </p>
                                        <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                            {item.result}
                                        </p>
                                    </div>
                                </Reveal>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    )
}

export default RacingSection
